import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { I18nContextValue, LocalizationResponse } from './types';
import {
  getCurrentLanguage,
  setCurrentLanguage,
  fetchLocalizations,
  flattenTranslations,
  interpolate,
  resolveKey,
} from './localization.service';

const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * Turn a translation key into readable text for the fallback path.
 * Keys that already look like sentences ('Are you sure?') are returned as-is;
 * namespaced PascalCase keys ('StepDetail:OpenDocument') become 'Open Document'.
 */
const humanizeKey = (key: string): string => {
  const withoutResource = key.includes('::') ? key.split('::')[1] : key;
  if (/\s/.test(withoutResource)) return withoutResource;
  const last = withoutResource.split(':').pop() ?? withoutResource;
  return last
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
};

interface I18nProviderProps {
  children: ReactNode;
}

/**
 * I18n Provider - Loads and provides translations from ABP localization API
 *
 * IMPORTANT: All user-facing text must use the t() function or Trans component.
 * Never hardcode labels.
 */
export const I18nProvider = ({ children }: I18nProviderProps) => {
  const [currentCulture, setCurrentCulture] = useState(getCurrentLanguage);
  const queryClient = useQueryClient();

  // Fetch localizations with TanStack Query
  const {
    data: localizationData,
    isLoading,
    error,
  } = useQuery<LocalizationResponse>({
    queryKey: ['localizations', currentCulture],
    queryFn: () => fetchLocalizations(currentCulture),
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    retry: 2,
  });

  // Flatten translations for easy lookup
  const translations = useMemo(() => {
    if (!localizationData?.resources) return {};
    return flattenTranslations(localizationData.resources);
  }, [localizationData?.resources]);

  // Available languages
  const availableLanguages = useMemo(() => {
    if (!localizationData?.languages) return [];
    return localizationData.languages
      .filter((lang) => lang.isEnabled !== false) // Include if isEnabled is true or undefined
      .map((lang) => ({
        cultureName: lang.cultureName,
        displayName: lang.displayName,
        flagIcon: lang.flagIcon,
      }));
  }, [localizationData?.languages]);

  // Whether current language is RTL
  const isRTL = localizationData?.currentCulture?.isRightToLeft ?? false;

  // Track if translations have been loaded at least once
  const hasLoadedTranslations = Object.keys(translations).length > 0;

  // Translation function
  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      // Resolve key format (handles ::key -> motr::key)
      const resolvedKey = resolveKey(key);

      // Try exact match first, then without resource prefix
      let translation = translations[resolvedKey];

      // If not found, try with default resource prefix (motr::key)
      if (!translation && !resolvedKey.includes('::')) {
        translation = translations[`motr::${resolvedKey}`];
      }

      // If not found and key has ::, try just the key part
      if (!translation && resolvedKey.includes('::')) {
        const keyWithoutResource = resolvedKey.split('::')[1];
        translation = translations[keyWithoutResource];
      }

      if (!translation) {
        // Only log missing translation in development AND after translations have loaded
        // This prevents warnings during initial render before translations are fetched
        if (import.meta.env.DEV && hasLoadedTranslations) {
          console.warn(`[i18n] Missing translation: ${key} (resolved: ${resolvedKey})`);
        }
        // Use defaultValue if provided, otherwise fall back to key
        const defaultValue = params?.defaultValue;
        if (defaultValue !== undefined) {
          return interpolate(String(defaultValue), params);
        }
        // Last resort: humanise the key so the user never sees a raw
        // 'Namespace:SomeKey' token (e.g. 'StepDetail:OpenDocument' -> 'Open Document').
        return interpolate(humanizeKey(key), params);
      }

      return interpolate(translation, params);
    },
    [translations, hasLoadedTranslations]
  );

  // Change language
  const setLanguage = useCallback(
    (cultureName: string) => {
      setCurrentLanguage(cultureName);
      setCurrentCulture(cultureName);
      // Refetch everything: translations, plus any API data that carries
      // server-localized text (step actions, group labels, status names).
      queryClient.invalidateQueries();
    },
    [queryClient]
  );

  // Update document direction for RTL support
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentCulture;
  }, [isRTL, currentCulture]);

  // Log loading errors
  useEffect(() => {
    if (error) {
      console.error('[i18n] Failed to load translations:', error);
    }
  }, [error]);

  const value: I18nContextValue = {
    translations,
    currentCulture,
    availableLanguages,
    isLoading,
    t,
    setLanguage,
    isRTL,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/**
 * Hook to access i18n context
 * @throws Error if used outside I18nProvider
 */
export const useI18nContext = (): I18nContextValue => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18nContext must be used within an I18nProvider');
  }
  return context;
};

export default I18nProvider;
